import theme from 'src/shared/theme';
import styled from 'styled-components';
import { Button } from '../buttons';
import { Icon } from '../icon';

/**
 * The three AI buttons every generation surface must reuse.
 *
 * `AiGenerateButton` is the CTA that starts a generation ("Generate with AI", "Customize with AI").
 * `AiPromptSendButton` is the round arrow that submits a prompt written in a textarea.
 * `AiIconButton` is the icon-only trigger used where a label does not fit (image overlays, toolbars).
 *
 * All three carry `theme.ai.gradient`: the first two paint it as a background, the third through the
 * `Sparkles` glyph. Do not re-implement any of them locally: before these existed the Backoffice
 * carried seven different "generate" buttons and two different send-button shapes.
 */

export const AiGenerateButton = styled(Button)`
    background: ${theme.ai.gradient} !important;
    border: 1px solid transparent !important;
    color: #fff !important;

    ${Icon} {
        color: #fff;
    }

    /* Same treatment as AiIconButton, for the CTAs that lead with a bare <Sparkles /> glyph. */
    && > svg {
        width: 16px;
        height: 16px;
        min-width: 16px;
        flex-shrink: 0;
    }

    && > svg path {
        fill: #ffffff;
    }

    &:hover:not([disabled]) {
        background: ${theme.ai.gradientHover} !important;
    }

    &[disabled] {
        background: #e5e7eb !important;
        color: #9ca3af !important;
        cursor: default;
    }

    &[disabled] ${Icon} {
        color: #9ca3af;
    }

    &[disabled] > svg path {
        fill: #9ca3af;
    }
`;

/**
 * Outline sibling of `AiGenerateButton`: transparent fill with a gradient border (double-background
 * trick) and gradient-colored text/glyph. Use it for the secondary action that sits next to the solid
 * `AiGenerateButton` (e.g. "Regenerate", "Edit description").
 */
export const AiOutlineButton = styled(Button)`
    background:
        linear-gradient(#fff, #fff) padding-box,
        ${theme.ai.gradient} border-box !important;
    border: 1px solid transparent !important;
    color: ${theme.ai.solid} !important;

    && > svg {
        width: 16px;
        height: 16px;
        min-width: 16px;
        flex-shrink: 0;
    }

    && > svg path {
        fill: ${theme.ai.solid};
    }

    &:hover:not([disabled]) {
        filter: brightness(0.98);
    }

    &[disabled] {
        opacity: 0.5;
        cursor: default;
    }
`;

/**
 * Icon-only trigger: `theme.ai.gradient` fills the box and the glyph sits on it in white. Render
 * `<Sparkles />` as the only child.
 *
 * Sizing comes from `Button`, so callers pass `$type='icon'` plus the `$size` their row uses. A
 * surface that needs a different box extends this component rather than restyling the gradient.
 *
 * The glyph is sized through `> svg` because `Button` already pins bare SVG children to 16px there,
 * and because `Sparkles` renders the SVG itself rather than wrapping one in an `Icon`. `min-width`
 * and `flex-shrink` are what `Icon` gives its own SVG: without them the button's padding squeezes a
 * bare SVG flex child down to the content box, whatever width it is given — and centring it needs
 * the flex alignment `Icon` would otherwise have supplied.
 *
 * `Sparkles` paints its paths with `fill='url(#…)'`, a presentation attribute: a CSS `fill` outranks
 * it, so the glyph turns white here without a second copy of the SVG.
 */
export const AiIconButton = styled(Button)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${theme.ai.gradient};
    border: 1px solid transparent;
    transition: filter 0.12s ease;

    && > svg {
        width: 16px;
        height: 16px;
        min-width: 16px;
        flex-shrink: 0;
    }

    && > svg path {
        fill: #ffffff;
    }

    &:hover:not([disabled]) {
        background: ${theme.ai.gradient};
        filter: brightness(1.06);
    }

    &[disabled] {
        background: #f3f4f6;
        cursor: default;
    }

    &[disabled] > svg path {
        fill: #d1d5db;
    }
`;

/**
 * `$size` keeps the shape circular at any diameter: chat rows need a small one, the AI Studio
 * prompt bar a large one. `$isGenerating` turns it into the cancel affordance.
 */
export const AiPromptSendButton = styled.button<{ $size?: number; $isGenerating?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: ${(props) => props.$size ?? 50}px;
    height: ${(props) => props.$size ?? 50}px;
    border: none;
    border-radius: 50%;
    background: ${(props) => (props.$isGenerating ? theme.ai.stop : theme.ai.gradient)};
    color: #fff;
    cursor: pointer;
    transition: transform 0.2s ease;

    svg {
        width: ${(props) => Math.round((props.$size ?? 50) * 0.48)}px;
        height: ${(props) => Math.round((props.$size ?? 50) * 0.48)}px;
        fill: #fff;
    }

    &:hover:not(:disabled) {
        transform: scale(1.05);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;

        &:hover:not(:disabled) {
            transform: none;
        }
    }
`;
